import { Search, Upload, X } from 'lucide-react'
import { useId } from 'react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/toggle-group'
import { DOCUMENT_STATE_FILTER, type DocumentStateFilter } from '../domain/group-documents'

export type DocumentToolbarProps = {
  search: string
  onSearchChange: (value: string) => void
  state: DocumentStateFilter
  onStateChange: (value: DocumentStateFilter) => void
  /** Ausente esconde el botón: la galería trae el suyo con otro rótulo. */
  onUpload?: () => void
  uploadLabel?: string
  uploadDisabled?: boolean
  uploadTitle?: string
}

/**
 * EL ENCABEZADO PEGAJOSO DEL CARTAPACIO: buscar, filtrar por estado, subir.
 *
 * ## Es chrome del BACK-OFFICE y tiene que parecerlo
 *
 * Debajo empieza el lienzo con la UI de la app; acá arriba viven los controles de administración,
 * con los primitivos de `shared/ui`. La frontera es deliberada: lo que se toma prestado de la app
 * es cómo se ve un DOCUMENTO, no cómo se ve un control. Un buscador dibujado con los tokens de la
 * app haría creer que el cliente también busca así, y no es cierto.
 *
 * ## Pegajoso, porque se scrollea mientras se cura
 *
 * Un cartapacio de cuarenta documentos son varias pantallas, y «Subir» al fondo de esa columna es
 * un botón que hay que ir a buscar. Va arriba y se queda. `top-0` contra el contenedor de scroll
 * de la pestaña, con el fondo del panel —no transparente— o las tarjetas se le verían por debajo.
 *
 * ## El filtro de estado es un grupo de tres, no un `Select`
 *
 * Son tres valores fijos y excluyentes, y el que está activo tiene que leerse SIN abrir nada: la
 * pregunta «¿estoy viendo todo?» se hace cada vez que la lista parece corta. Un `Select` esconde
 * la respuesta detrás de un click, y además ocupa el mismo ancho que las tres opciones juntas a
 * este tamaño.
 */
export function DocumentToolbar({
  search,
  onSearchChange,
  state,
  onStateChange,
  onUpload,
  uploadLabel = 'Subir',
  uploadDisabled = false,
  uploadTitle
}: DocumentToolbarProps) {
  const searchId = useId()

  return (
    <div className="sticky top-0 z-30 flex flex-col gap-2 border-b border-border bg-background px-3 pt-3 pb-2">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id={searchId}
            type="search"
            value={search}
            // `autoComplete="off"` y `spellCheck={false}`: son nombres de archivo, no prosa, y el
            // subrayado rojo bajo «IUSI» o «DPI» se lee como un error del dato.
            autoComplete="off"
            spellCheck={false}
            placeholder="Buscar por nombre…"
            aria-label="Buscar en el cartapacio"
            className="h-8 pr-8 pl-8 text-xs"
            onChange={(event) => {
              onSearchChange(event.target.value)
            }}
          />
          {search.length > 0 ? (
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              aria-label="Limpiar la búsqueda"
              className="absolute top-1/2 right-1 -translate-y-1/2"
              onClick={() => {
                onSearchChange('')
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>

        {onUpload ? (
          <Button
            type="button"
            size="sm"
            className="h-8 shrink-0 gap-1.5 transition-transform duration-150 ease-out active:scale-[0.97]"
            disabled={uploadDisabled}
            title={uploadTitle}
            onClick={onUpload}
          >
            <Upload className="h-3.5 w-3.5" />
            {uploadLabel}
          </Button>
        ) : null}
      </div>

      <ToggleGroup
        type="single"
        size="sm"
        value={state}
        // Radix devuelve `''` al deseleccionar el activo. Eso no es un cuarto estado: volver a
        // apretar «Publicados» tiene que dejar la lista como está, no vaciarla.
        onValueChange={(value) => {
          if (value) onStateChange(value as DocumentStateFilter)
        }}
        className="justify-start gap-1"
      >
        <ToggleGroupItem value={DOCUMENT_STATE_FILTER.all} className="h-7 px-2 text-xs">
          Todos
        </ToggleGroupItem>
        <ToggleGroupItem value={DOCUMENT_STATE_FILTER.public} className="h-7 px-2 text-xs">
          Publicados
        </ToggleGroupItem>
        <ToggleGroupItem value={DOCUMENT_STATE_FILTER.internal} className="h-7 px-2 text-xs">
          Internos
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  )
}
